'use client'

import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, ElementType, ReactNode, Ref } from 'react'
import styles from '../landing.module.css'

interface LandingRevealOnViewProps {
  children: ReactNode
  as?: ElementType
  className?: string
  delayMs?: number
}

/**
 * Fades/slides content in the first time it enters the viewport, then
 * leaves it visible permanently so it never replays on subsequent
 * scrolling within the same page visit.
 */
export function LandingRevealOnView({
  children,
  as: Component = 'div',
  className = '',
  delayMs = 0,
}: LandingRevealOnViewProps) {
  const elementRef = useRef<HTMLElement | null>(null)
  const [isRevealed, setIsRevealed] = useState(false)

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting) {
          setIsRevealed(true)
          observer.unobserve(element)
        }
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const style: CSSProperties | undefined = delayMs ? { transitionDelay: `${delayMs}ms` } : undefined

  return (
    <Component
      ref={elementRef as Ref<HTMLElement>}
      className={`${styles.reveal} ${isRevealed ? styles.revealVisible : ''} ${className}`.trim()}
      style={style}
    >
      {children}
    </Component>
  )
}
